import {ComboboxProvider, SelectProvider} from '@ariakit/react'
import {DiseaseExtended} from '../../types'
import {useTranslation} from '@/translations/i18n'
import {useMemo, useRef, useState} from 'react'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectItem,
    SelectList,
    SelectTrigger
} from '@/components/ui/select-atoms/SelectAtoms'
import {InputStatusIcon} from '@/components/ui/input-status-icon/InputStatusIcon'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {useDisease} from '../../services/useDisease'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {PatientAgeRange} from '@/types'

interface InputSelectDiseaseProps {
    label: string
    diseaseId?: DiseaseExtended['id']
    defaultValue?: DiseaseExtended
    onChange: (value: DiseaseExtended['id'] | null) => void
    isOptional?: boolean
    placeholder?: string
    serviceType: Exclude<PatientAgeRange, 'evolutionary_age_adults'>
    errorMessage?: string
    onClear?: () => void
}

export const InputSelectDisease = ({
    label,
    diseaseId,
    placeholder,
    defaultValue,
    isOptional,
    onChange,
    errorMessage,
    onClear
}: InputSelectDiseaseProps) => {
    const {t} = useTranslation()
    const inputDiseaseRef = useRef(null)
    const [searchValue, setSearchValue] = useState('')
    const [selectedDisease, setSelectedDisease] = useState<null | DiseaseExtended>(defaultValue ?? null)

    const selectedDiseaseGroupQuery = useDisease(diseaseId, {
        enabled: !!diseaseId
    })

    const diseasesOptions = useMemo(
        () =>
            diseaseId && selectedDiseaseGroupQuery.data?.children
                ? selectedDiseaseGroupQuery.data.children.filter(group =>
                      group.name.toLowerCase().includes(searchValue.toLowerCase())
                  )
                : [],
        [selectedDiseaseGroupQuery.data, searchValue]
    )

    return (
        <ComboboxProvider setValue={setSearchValue} resetValueOnHide>
            <SelectProvider
                value={selectedDisease?.id ?? ''}
                setOpen={() => setSearchValue('')}
                setValue={value => {
                    const disease = diseasesOptions.find(disease => disease.id == value) ?? null
                    setSelectedDisease(disease)
                    onChange(disease?.id || null)
                }}
            >
                <Flexbox direction="column" gap={1.5} fullWidth>
                    <Label>
                        {label} {isOptional && <span className="description">{t('commons:optional')}</span>}
                    </Label>
                    <SelectTrigger
                        placeholder={placeholder || t('commons:select')}
                        fullWidth
                        value={selectedDisease?.id ?? ''}
                        hasError={!!errorMessage}
                        displayValue={() => (
                            <Flexbox fullWidth justify="space-between" align="center">
                                {selectedDisease?.name}
                                {onClear && (
                                    <XCloseIcon
                                        onClick={e => {
                                            e.preventDefault()
                                            e.stopPropagation()
                                            setSelectedDisease(null)
                                            onClear()
                                        }}
                                    />
                                )}
                            </Flexbox>
                        )}
                    />
                    <Flexbox align="center" gap={errorMessage ? 1.5 : 0}>
                        <InputStatusIcon hasError={!!errorMessage} />
                        <InputHelpText error={errorMessage} />
                    </Flexbox>
                    <SelectComboboxPopover sameWidth>
                        <SelectComboboxInput ref={inputDiseaseRef} placeholder={t('commons:search_placeholder')} />
                        <SelectComboboxList>
                            {selectedDiseaseGroupQuery.isLoading ? (
                                <>
                                    {Array.from({length: 6}, (_, index) => (
                                        <Skeleton key={index} height={32} />
                                    ))}
                                </>
                            ) : selectedDiseaseGroupQuery.isError ? (
                                <InputHelpText error={t('errors:default')} />
                            ) : diseasesOptions.length > 0 ? (
                                <SelectList>
                                    {diseasesOptions.map((option, index) => (
                                        <SelectItem hasItemCheckbox key={index} value={option.id}>
                                            {option.name}
                                        </SelectItem>
                                    ))}
                                </SelectList>
                            ) : (
                                <InputHelpText helpText={t('commons:no_results')} />
                            )}
                        </SelectComboboxList>
                    </SelectComboboxPopover>
                </Flexbox>
            </SelectProvider>
        </ComboboxProvider>
    )
}
